import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import WorkerDashboard from "@/pages/WorkerDashboard";
import Assessment from "@/pages/Assessment";
import Resume from "@/pages/Resume";
import EmployerDashboard from "@/pages/EmployerDashboard";
import Network from "@/pages/Network";
import Messages from "@/pages/Messages";
import Recommendations from "@/pages/Recommendations";
import Account from "@/pages/Account";
import BadgeVerify from "@/pages/BadgeVerify";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/PasswordAuth";
import Communities from "@/pages/Communities";
import CommunityDetail from "@/pages/CommunityDetail";

const Protected = ({ role, children }) => {
  const { currentUser, authState } = useApp();
  const location = useLocation();
  if (authState === "checking") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!currentUser) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (role && currentUser.role !== role) {
    return <Navigate to={currentUser.role === "employer" ? "/employer" : "/worker"} replace />;
  }
  return children;
};

function AppShell() {
  const location = useLocation();
  // Handle Google OAuth callback via hash (before any protected route decisions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/worker" element={<Protected role="worker"><WorkerDashboard /></Protected>} />
      <Route path="/worker/assessment" element={<Protected role="worker"><Assessment /></Protected>} />
      <Route path="/employer" element={<Protected role="employer"><EmployerDashboard /></Protected>} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/messages/:otherId" element={<Protected><Messages /></Protected>} />
      <Route path="/recommendations" element={<Protected><Recommendations /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/communities" element={<Communities />} />
      <Route path="/communities/:slug" element={<CommunityDetail />} />
      <Route path="/resume/:workerId" element={<Resume />} />
      <Route path="/verify/:hash" element={<BadgeVerify />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
